import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { searchApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Building2, FolderKanban, CheckSquare, Users, StickyNote, Calendar } from "lucide-react";

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [res, setRes] = useState(null);

  useEffect(() => {
    if (!q || q.length < 2) { setRes(null); return; }
    setSp({ q });
    const t = setTimeout(async () => { const r = await searchApi.global(q); setRes(r.data); }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const Section = ({ title, Icon, items, render }) => items?.length > 0 && (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-white/50"/>
        <h2 className="font-display text-lg">{title} <span className="text-white/40 text-sm">({items.length})</span></h2>
      </div>
      <div className="space-y-2">{items.map(render)}</div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto p-8" data-testid="search-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">Search</h1>
        <div className="relative mb-8">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-white/40"/>
          <Input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Search everything…" className="pl-11 h-12 text-base bg-white/5 border-white/10" data-testid="search-input"/>
        </div>

        {res && (
          <div>
            <Section title="Entities" Icon={Building2} items={res.entities} render={(e) => (
              <Link key={e.id} to={`/entity/${e.id}`} className="block p-4 rounded-lg border border-white/10 bg-[#151A23] hover:border-white/25"><div className="font-medium">{e.name}</div><div className="text-xs text-white/50">{e.description}</div></Link>
            )}/>
            <Section title="Projects" Icon={FolderKanban} items={res.projects} render={(p) => (
              <Link key={p.id} to={`/entity/${p.entity_id}?tab=projects`} className="block p-4 rounded-lg border border-white/10 bg-[#151A23] hover:border-white/25"><div className="font-medium">{p.name}</div><div className="text-xs text-white/50">{p.status} · {p.priority}</div></Link>
            )}/>
            <Section title="Tasks" Icon={CheckSquare} items={res.tasks} render={(t) => (
              <Link key={t.id} to={`/entity/${t.entity_id}?tab=tasks`} className="block p-4 rounded-lg border border-white/10 bg-[#151A23] hover:border-white/25"><div className="font-medium">{t.name}</div><div className="text-xs text-white/50">{t.status} · {t.priority}{t.due_date ? ` · due ${t.due_date}` : ""}</div></Link>
            )}/>
            <Section title="Contacts" Icon={Users} items={res.contacts} render={(c) => (
              <Link key={c.id} to={`/contacts/${c.id}`} className="block p-4 rounded-lg border border-white/10 bg-[#151A23] hover:border-white/25"><div className="font-medium">{c.full_name}</div><div className="text-xs text-white/50">{c.email || c.role}</div></Link>
            )}/>
            <Section title="Notes & Decisions" Icon={StickyNote} items={res.notes} render={(n) => (
              <Link key={n.id} to={`/entity/${n.entity_id}?tab=notes`} className="block p-4 rounded-lg border border-white/10 bg-[#151A23] hover:border-white/25"><div className="font-medium">{n.title}</div><div className="text-xs text-white/50">{n.entry_type} · {new Date(n.created_at).toLocaleDateString()}</div></Link>
            )}/>
            <Section title="Calendar Events" Icon={Calendar} items={res.events} render={(e) => (
              <Link key={e.id} to={`/calendar`} className="block p-4 rounded-lg border border-white/10 bg-[#151A23] hover:border-white/25"><div className="font-medium">{e.title}</div><div className="text-xs text-white/50">{new Date(e.starts_at).toLocaleString()} · {e.event_type}</div></Link>
            )}/>
            {Object.values(res).every(v => (v || []).length === 0) && <div className="text-center py-12 text-white/40">No results for "{q}"</div>}
          </div>
        )}
        {!res && q.length > 0 && q.length < 2 && <div className="text-white/40 text-sm">Keep typing…</div>}
      </div>
    </div>
  );
}
